// ============================================
// ユーティリティ関数
// ============================================

import type p5 from 'p5';
import { getP5 } from '../core/p5-instance';
import { getSelectedDifficulty, getPowerLevel, getSelectedShip, getWave, getPanelSlideProgress } from '../store/index';
import { CONFIG } from '../config/game-config';
import { getCurrentStageCfg as getBgCurrentStageCfg } from '../features/background/background';
import { DifficultyConfig, StageConfig } from '../types/game-types';

/**
 * p5.js color オブジェクトを16進数カラーコードに変換
 * @param c - p5.jsのcolorオブジェクト
 * @returns 16進数カラーコード (#RRGGBB)
 */
export function colorToHex(c: p5.Color): string {
    const p = getP5();
    const r = p.floor(p.red(c));
    const g = p.floor(p.green(c));
    const b = p.floor(p.blue(c));
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * イージング関数: EaseInOutCubic
 * @param t - 0.0〜1.0の範囲の時間パラメータ
 * @returns イージング適用後の値
 */
export function easeInOutCubic(t: number): number {
    const p = getP5();
    return t < 0.5 ? 4 * t * t * t : 1 - p.pow(-2 * t + 2, 3) / 2;
}

/**
 * イージング関数: EaseInCubic
 * @param t - 0.0〜1.0の範囲の時間パラメータ
 * @returns イージング適用後の値
 */
export function easeInCubic(t: number): number {
    return t * t * t;
}

/**
 * イージング関数: EaseOutCubic
 * @param t - 0.0〜1.0の範囲の時間パラメータ
 * @returns イージング適用後の値
 */
export function easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
}

/**
 * 現在選択されている難易度設定を取得
 * @returns 難易度設定オブジェクト
 */
export function getDiffConfig(): DifficultyConfig {
    return CONFIG.difficulty[getSelectedDifficulty()];
}

/**
 * HP割合に応じてダメージを受けた色を計算
 * @param originalColor - 元の色
 * @param hpRatio - HP割合（0.0〜1.0）
 * @returns ダメージ適用後の色
 */
export function getDamageColor(originalColor: p5.Color, hpRatio: number): p5.Color {
    const p = getP5();
    const r = p.red(originalColor);
    const g = p.green(originalColor);
    const b = p.blue(originalColor);

    // HP割合に応じて少しだけ暗くなる（控えめな変化）
    const darkFactor = 0.7 + hpRatio * 0.3;  // 0.7～1.0の範囲
    const damageR = r * darkFactor + (1 - hpRatio) * 30;  // 少し赤みを足す
    const damageG = g * darkFactor;
    const damageB = b * darkFactor;

    return p.color(p.min(255, damageR), damageG, damageB);
}

/**
 * 星の瞬き効果を計算
 * @param star - 星オブジェクト（twinkleSpeed, twinkleOffsetプロパティを持つ）
 * @param base - ベース輝度（デフォルト: 0.5）
 * @param amplitude - 振幅（デフォルト: 0.5）
 * @param speedMul - 速度倍率（デフォルト: 1）
 * @returns 瞬き適用後の輝度
 */
export function starTwinkle(star: { twinkleSpeed: number; twinkleOffset: number }, base: number = 0.5, amplitude: number = 0.5, speedMul: number = 1): number {
    const p = getP5();
    // 既存式と等価: base + amplitude * sin(frameCount * star.twinkleSpeed * speedMul + star.twinkleOffset)
    return base + amplitude * p.sin(p.frameCount * star.twinkleSpeed * speedMul + star.twinkleOffset);
}

/**
 * 現在のステージ設定を取得
 * @returns ステージ設定オブジェクト
 */
export function getCurrentStageCfg(): StageConfig {
    return getBgCurrentStageCfg();
}

/**
 * 次のパワーレベルに必要なパワーストック数を取得
 * @returns 必要なパワーストック数
 */
export function getPowerRequiredForNextLevel(): number {
    const powerLevel = getPowerLevel();
    const selectedShip = getSelectedShip();
    const ships = CONFIG.ships as unknown as Record<string, { powerMaxLevel: number, powerRequired: number[] }>;
    const shipCfg = ships[selectedShip];

    if (powerLevel >= shipCfg.powerMaxLevel) {
        return 0; // Already at max level
    }

    return shipCfg.powerRequired[powerLevel - 1] || 1;
}

/**
 * 現在のウェーブをクリアするために必要な敵撃破数を取得
 * @returns 必要な敵撃破数
 */
export function getEnemiesRequiredForWave(): number {
    const p = getP5();
    const wave = getWave();

    // Base enemies + scaling per wave
    const required = CONFIG.wave.baseEnemiesPerWave + (wave - 1) * CONFIG.wave.enemiesIncreasePerWave;
    return p.min(required, CONFIG.wave.maxEnemiesPerWave);
}

/**
 * アビリティボタンの配置情報を取得（右パネル内）
 * @returns ボタンの中心座標(x, y)とサイズ(width, height)
 */
export function getAbilityButtonBounds(): { x: number, y: number, size: number, height: number } {
    const p = getP5();
    // 右パネル中央に配置
    return {
        x: CONFIG.layout.gameAreaX + CONFIG.width + CONFIG.layout.panelWidth / 2 + getPanelSlideOffset(),
        y: p.height - CONFIG.ui.abilityButton.bottomOffset,
        size: CONFIG.ui.abilityButton.size,
        height: CONFIG.ui.abilityButton.height
    };
}

// ============================================
// ランク計算
// ============================================

/** スコアとウェーブに基づいてランクを計算（CONFIG.ranksと統一） */
export function calculateRank(score: number, wave: number): string {
    for (const rank of CONFIG.ranks) {
        if (score >= rank.minScore && wave >= rank.minWave) {
            return rank.name;
        }
    }
    return CONFIG.ranks[CONFIG.ranks.length - 1].name;
}

/** ランクに応じた色を取得（CONFIG.ranksと統一） */
export function getRankColor(rank: string): readonly [number, number, number] {
    const found = CONFIG.ranks.find(r => r.name === rank);
    return found ? found.color : [150, 150, 150];
}

// ============================================
// ゲームエリア座標ヘルパー関数
// ============================================

/**
 * ゲームエリアの開始X座標を取得
 */
export function getGameAreaX(): number {
    return CONFIG.layout.gameAreaX;
}

/**
 * ゲームエリアの終了X座標を取得
 */
export function getGameAreaEndX(): number {
    return CONFIG.layout.gameAreaX + CONFIG.width;
}

/**
 * ゲームエリアの中央X座標を取得
 */
export function getGameAreaCenterX(): number {
    return CONFIG.layout.gameAreaX + CONFIG.width / 2;
}

/**
 * パネルスライドオフセットを取得（0=完全に隠れている, panelWidth=オフセットなし）
 */
function getPanelSlideOffset(): number {
    return CONFIG.layout.panelWidth * (1 - easeOutCubic(getPanelSlideProgress()));
}

/**
 * 左パネルの中央X座標を取得
 */
export function getLeftPanelCenterX(): number {
    return CONFIG.layout.panelWidth / 2 - getPanelSlideOffset();
}

/**
 * 右パネルの開始X座標を取得
 */
export function getRightPanelX(): number {
    return CONFIG.layout.gameAreaX + CONFIG.width + getPanelSlideOffset();
}

/**
 * 右パネルの中央X座標を取得
 */
export function getRightPanelCenterX(): number {
    return CONFIG.layout.gameAreaX + CONFIG.width + CONFIG.layout.panelWidth / 2 + getPanelSlideOffset();
}
